import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Card, Col, DatePicker, Empty, Row, Select, Space, Spin, Table, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import firebase from "../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import _ from "lodash";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FIRESTORE_CUSTOMER_SEGMENTS_TABLE,
  FIRESTORE_USER_ANALYTICS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";

const db = firebase.firestore();

const Container = styled.div`
  padding: 1rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const SummaryCard = styled(Card)`
  height: 100%;
`;

const TrendCard = styled(Card)`
  height: 100%;
  max-height: 800px;
`;

const MetricLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
`;

const COLORS = [
  "#3366CC",
  "#DC3912",
  "#FF9900",
  "#109618",
  "#990099",
  "#0099C6",
  "#DD4477",
  "#66AA00",
  "#B82E2E",
  "#316395",
  "#994499",
]; // fallback palette

const dateFormat = "DD-MM-YYYY";

const CustomerSegmentsDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]); // [{id, name, marketingFocus, newCustomer, rfmScores}]
  const [segmentCounts, setSegmentCounts] = useState({}); // {segmentId: count}
  const [trendData, setTrendData] = useState([]); // [{date, SegmentName1: n, ...}]
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [yesterdayMetrics, setYesterdayMetrics] = useState({}); // {segmentId: metrics}
  const [period, setPeriod] = useState("last_30_days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [marketingFocusFilter, setMarketingFocusFilter] = useState([]); // strings

  const getRangeForPeriod = (p) => {
    const today = dayjs();
    switch (p) {
      case "last_7_days":
        return { start: today.subtract(7, "day"), end: today.subtract(1, "day") };
      case "last_30_days":
        return { start: today.subtract(30, "day"), end: today.subtract(1, "day") };
      case "last_90_days":
        return { start: today.subtract(90, "day"), end: today.subtract(1, "day") };
      case "last_180_days":
        return { start: today.subtract(180, "day"), end: today.subtract(1, "day") };
      case "last_365_days":
        return { start: today.subtract(365, "day"), end: today.subtract(1, "day") };
      case "custom":
        return { start: startDate, end: endDate };
      default:
        return { start: today.subtract(30, "day"), end: today.subtract(1, "day") };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1) Load segment definitions
        const segmentsSnap = await db.collection(FIRESTORE_CUSTOMER_SEGMENTS_TABLE).get();
        const segs = segmentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSegments(segs);

        // 2) Current counts by segment from userAnalytics.currentRfm.segment
        const uaSnap = await db.collection(FIRESTORE_USER_ANALYTICS_TABLE).get();
        const counts = {};
        uaSnap.docs.forEach((d) => {
          const ua = d.data();
          const seg = _.get(ua, "currentRfm.segment");
          if (seg) counts[seg] = (counts[seg] || 0) + 1;
        });
        setSegmentCounts(counts);

        // 3) Build trend over selected range using history subcollections (users array length)
        const { start, end } = getRangeForPeriod(period === "custom" ? "custom" : period);
        const useStart = start || dayjs().subtract(30, "day");
        const useEnd = end || dayjs().subtract(1, "day");
        const dateRange = [];
        for (let d = useStart; d.isBefore(useEnd.add(1, "day")); d = d.add(1, "day")) {
          dateRange.push(d.format(dateFormat));
        }

        const perSegmentHistory = {};
        const yesterdayPerSegment = {};

        // Fetch all history docs per segment
        for (const seg of segs) {
          const histSnap = await db
            .collection(FIRESTORE_CUSTOMER_SEGMENTS_TABLE)
            .doc(seg.id)
            .collection("history")
            .get();
          const byDate = {};
          histSnap.docs.forEach((doc) => {
            const idDate = doc.id; // expected like 01-08-2025
            const data = doc.data();
            const users = Array.isArray(data.users) ? data.users : [];
            byDate[idDate] = users.length;
            if (idDate === dayjs().subtract(1, "day").format(dateFormat)) {
              yesterdayPerSegment[seg.id] = data.metrics || null;
            }
          });
          perSegmentHistory[seg.id] = byDate;
        }

        const trend = dateRange.map((dateStr) => {
          const entry = { date: dateStr };
          segs.forEach((seg) => {
            entry[seg.name || seg.id] = perSegmentHistory[seg.id]?.[dateStr] || 0;
          });
          return entry;
        });

        setTrendData(trend);
        setYesterdayMetrics(yesterdayPerSegment);

        const sortedByCount = _.orderBy(
          segs.map((s) => ({ seg: s, count: counts[s.id] || counts[s.name] || 0 })),
          [(x) => x.count],
          ["desc"],
        );
        setSelectedSegments(sortedByCount.map((x) => x.seg.id));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load customer segments dashboard", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate]);

  console.log(trendData);
  console.log(yesterdayMetrics);

  const summary = useMemo(() => {
    const total = _.sum(Object.values(segmentCounts));
    const byName = {};
    segments.forEach((s) => {
      const key = s.name || s.id;
      const val = segmentCounts[s.id] ?? segmentCounts[s.name] ?? 0;
      byName[key] = val;
    });
    return { total, byName };
  }, [segmentCounts, segments]);

  const filteredSegments = useMemo(() => {
    let list = segments;
    if (marketingFocusFilter.length > 0)
      list = list.filter(
        (s) => Array.isArray(s.marketingFocus) && s.marketingFocus.some((f) => marketingFocusFilter.includes(f)),
      );
    if (selectedSegments.length > 0) list = list.filter((s) => selectedSegments.includes(s.id));
    return list;
  }, [segments, marketingFocusFilter, selectedSegments]);

  const allMarketingFocusOptions = useMemo(() => {
    const set = new Set();
    segments.forEach((s) => (s.marketingFocus || []).forEach((f) => set.add(f)));
    return Array.from(set);
  }, [segments]);

  const segmentTableData = useMemo(() => {
    return filteredSegments.map((s) => {
      const key = s.id;
      const metrics = yesterdayMetrics[key];
      const count = segmentCounts[s.id] ?? segmentCounts[s.name] ?? 0;
      return {
        key,
        name: s.name || key,
        count,
        newCustomer: s.newCustomer ? "Yes" : "No",
        marketingFocus: s.marketingFocus || [],
        rfmScores: (s.rfmScores || []).join(", "),
        ...(metrics?.analysisPeriod
          ? {
              analysisPeriod: metrics.analysisPeriod,
              averageDaysSinceLastOrder: metrics.averageDaysSinceLastOrder,
              averageOrderValue: metrics.averageOrderValue,
              averageOrdersInPeriod: metrics.averageOrdersInPeriod,
              averageOrdersPerMonth: metrics.averageOrdersPerMonth,
              ordersInPeriod: metrics.ordersInPeriod,
              totalSpentInPeriod: metrics.totalSpentInPeriod,
            }
          : {}),
      };
    });
  }, [filteredSegments, yesterdayMetrics, segmentCounts]);

  const columns = [
    { title: t("segment"), dataIndex: "name", key: "name" },
    {
      title: t("count"),
      dataIndex: "count",
      key: "count",
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: t("marketing_focus"),
      dataIndex: "marketingFocus",
      key: "marketingFocus",
      render: (vals) => (
        <>
          {(vals || []).map((v) => (
            <Tag key={v}>{v}</Tag>
          ))}
        </>
      ),
    },
    {
      title: t("rfm_scores"),
      dataIndex: "rfmScores",
      key: "rfmScores",
      render: (val) => <span style={{ maxHeight: 100, overflow: "scroll" }}>{val}</span>,
    },
    { title: t("analysis_period"), dataIndex: "analysisPeriod", key: "analysisPeriod" },
    {
      title: t("avg_days_since_last_order"),
      dataIndex: "averageDaysSinceLastOrder",
      key: "averageDaysSinceLastOrder",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val?.toFixed(2).replace(".", ",")}</span>,
      sorter: (a, b) => a.averageDaysSinceLastOrder - b.averageDaysSinceLastOrder,
    },
    {
      title: t("avg_order_value"),
      dataIndex: "averageOrderValue",
      key: "averageOrderValue",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val ? CURRENCY + formatPrice(val) : "-"}</span>,
      sorter: (a, b) => a.averageOrderValue - b.averageOrderValue,
    },
    {
      title: t("avg_orders_in_period"),
      dataIndex: "averageOrdersInPeriod",
      key: "averageOrdersInPeriod",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val?.toFixed(2).replace(".", ",")}</span>,
      sorter: (a, b) => a.averageOrdersInPeriod - b.averageOrdersInPeriod,
    },
    {
      title: t("avg_orders_per_month"),
      dataIndex: "averageOrdersPerMonth",
      key: "averageOrdersPerMonth",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val?.toFixed(2).replace(".", ",")}</span>,
      sorter: (a, b) => a.averageOrdersPerMonth - b.averageOrdersPerMonth,
    },
    {
      title: t("orders_in_period"),
      dataIndex: "ordersInPeriod",
      key: "ordersInPeriod",
      sorter: (a, b) => a.ordersInPeriod - b.ordersInPeriod,
    },
    {
      title: t("total_spent_in_period"),
      dataIndex: "totalSpentInPeriod",
      key: "totalSpentInPeriod",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val ? CURRENCY + formatPrice(val) : "-"}</span>,
      sorter: (a, b) => a.totalSpentInPeriod - b.totalSpentInPeriod,
    },
  ];

  const trendSeries = useMemo(() => {
    // Map selected segment ids to segment names for display
    const idToName = _.fromPairs(segments.map((s) => [s.id, s.name || s.id]));
    const ids = selectedSegments.length > 0 ? selectedSegments : filteredSegments.map((s) => s.id);
    return ids.map((id, i) => ({ id, name: idToName[id], color: COLORS[i % COLORS.length] }));
  }, [selectedSegments, segments, filteredSegments]);

  return (
    <Container>
      <Title>{t("customer_segments")}</Title>
      {loading ? (
        <Spin />
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} lg={8}>
                <SectionTitle>{t("date_range")}</SectionTitle>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Select
                    value={period}
                    onChange={(val) => setPeriod(val)}
                    options={[
                      { label: t("last_7_days"), value: "last_7_days" },
                      { label: t("last_30_days"), value: "last_30_days" },
                      { label: t("last_90_days"), value: "last_90_days" },
                      { label: t("last_180_days"), value: "last_180_days" },
                      { label: t("last_365_days"), value: "last_365_days" },
                      { label: t("custom_range"), value: "custom" },
                    ]}
                    style={{ width: "100%" }}
                  />
                  {period === "custom" && (
                    <DatePicker.RangePicker
                      value={[startDate, endDate]}
                      onChange={(vals) => {
                        setStartDate(vals?.[0] || null);
                        setEndDate(vals?.[1] || null);
                      }}
                      format={dateFormat}
                      allowClear
                      style={{ width: "100%" }}
                    />
                  )}
                </Space>
              </Col>
              <Col xs={24} md={12} lg={16}>
                <SectionTitle>{t("filters")}</SectionTitle>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder={t("marketing_focus_filter")}
                    value={marketingFocusFilter}
                    onChange={setMarketingFocusFilter}
                    options={allMarketingFocusOptions.map((f) => ({ label: f, value: f }))}
                    style={{ width: "100%" }}
                  />
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder={t("segments_filter")}
                    value={selectedSegments}
                    onChange={setSelectedSegments}
                    options={segments.map((s) => ({ value: s.id, label: s.name || s.id }))}
                    style={{ width: "100%" }}
                  />
                </Space>
              </Col>
            </Row>
          </Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <SummaryCard>
                <MetricLabel>{t("total_customers")}</MetricLabel>
                <MetricValue>{summary.total}</MetricValue>
              </SummaryCard>
            </Col>
            {filteredSegments.slice(0, 3).map((s, idx) => (
              <Col xs={24} sm={12} md={6} key={s.id}>
                <SummaryCard>
                  <MetricLabel>{s.name || s.id}</MetricLabel>
                  <MetricValue>{summary.byName[s.name || s.id] || 0}</MetricValue>
                </SummaryCard>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <TrendCard
                title={
                  <div
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBlock: 10 }}
                  >
                    <SectionTitle style={{ margin: 0 }}>{t("segment_size_trends_last_30_days")}</SectionTitle>
                    <div>
                      <Tooltip title={t("select_segments_to_compare")}>
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ maxWidth: 400 }}
                          placeholder={t("select_segments")}
                          value={selectedSegments}
                          onChange={(vals) => setSelectedSegments(vals)}
                          optionFilterProp="label"
                          options={segments.map((s) => ({ value: s.id, label: s.name || s.id }))}
                        />
                      </Tooltip>
                    </div>
                  </div>
                }
              >
                {trendData.length === 0 ? (
                  <Empty />
                ) : (
                  <div style={{ width: "100%", height: 340 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <RechartsTooltip />
                        {/*<Legend />*/}
                        {trendSeries.map((s) => (
                          <Line
                            key={s.id}
                            type="monotone"
                            dataKey={segments.find((x) => x.id === s.id)?.name || s.id}
                            stroke={s.color}
                            dot={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </TrendCard>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card>
                <SectionTitle>{t("segment_details_today")}</SectionTitle>
                <Table
                  size="small"
                  dataSource={segmentTableData}
                  columns={columns}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1200 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default CustomerSegmentsDashboard;
